import Grid from '@mui/material/Grid';
import { Dispatch, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { AnyAction } from 'redux';
import {
  changeClosingFilterCountry,
  changeClosingFilterEndBusinessDate,
  changeClosingFilterStartBusinessDate,
  changeClosingFilterViewMode,
  changeFilterStore,
  findClosingCollectionsRequested,
  findClosingSalesRequested,
  masterDataSelector,
  reportClosingCollectionsLoadingSelector,
  reportClosingFilterEndBusinessDateSelector,
  reportClosingFilterStartBusinessDateSelector,
  reportClosingFilterViewModeSelector,
  reportClosingSalesLoadingSelector,
} from '../../state';

import { ReportActionsGridItem, ReportFilterActions, ReportFilters } from '../ReportFilters';
import { FilterPanel, StatusChip } from '../Visual';

import { dateResponseMap } from '../../helpers';
import { AppPermissions, ClosingViewMode, MasterData, RemoteResource, Stores } from '../../types';
import { PermissionsProvider } from '../Permissions';
import {
  BusinessDateRangeFilter,
  CauseTypeFilter,
  CountryFilter,
  FederativeEntityFilter,
  FranchiseTypeFilterRedux,
  StoreFilter,
  ViewModeFilter,
} from './filters';

const handleSearchParams = (
  dispatch: Dispatch<AnyAction>,
  searchParams: URLSearchParams,
  masterData: RemoteResource<MasterData | undefined>
) => {
  const viewMode = (searchParams.get('viewMode') || 'consolidated') as ClosingViewMode;
  const country = searchParams.get('country');
  const stores = searchParams.get('stores')?.split(',');
  const startDate = dateResponseMap(searchParams.get('startDate')) as Date;
  const endDate = dateResponseMap(searchParams.get('endDate')) as Date;

  if (country && startDate && endDate) {
    dispatch(changeClosingFilterViewMode(viewMode));
    dispatch(changeClosingFilterStartBusinessDate(startDate));
    dispatch(changeClosingFilterEndBusinessDate(endDate));
    if (country) {
      const masterDataCountries = masterData.data?.countries || [];
      const filterCountry = masterDataCountries.find(i => i.countryCode === country);
      if (filterCountry) dispatch(changeClosingFilterCountry([filterCountry]));
    }
    if (stores) {
      const masterDataStores = masterData.data?.stores || [];
      const filterStores = stores.map(storeAcronym =>
        masterDataStores.find(i => i.countryCode === country && i.storeAcronym === storeAcronym)
      );
      dispatch(changeFilterStore(filterStores as Stores[]));
    }
    if ([ClosingViewMode.SALES, ClosingViewMode.CONSOLIDATED].includes(viewMode)) {
      dispatch(findClosingSalesRequested(undefined, startDate, endDate));
    }
    if ([ClosingViewMode.COLLECTIONS, ClosingViewMode.CONSOLIDATED].includes(viewMode)) {
      dispatch(findClosingCollectionsRequested(undefined, endDate));
    }
  }
};

const ClosingSearch = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const masterData = useSelector(masterDataSelector);
  const viewMode = useSelector(reportClosingFilterViewModeSelector);
  const startBusinessDate = useSelector(reportClosingFilterStartBusinessDateSelector);
  const endBusinessDate = useSelector(reportClosingFilterEndBusinessDateSelector);
  const country = true;
  const applyDisabled = !(startBusinessDate.accept && endBusinessDate.accept);
  const businessDateRequiredIncomplete = applyDisabled;

  useEffect(() => {
    handleSearchParams(dispatch, searchParams, masterData);
  }, [dispatch, masterData, searchParams]);

  const applyAction = useCallback(() => {
    if (viewMode === ClosingViewMode.SALES) {
      return [findClosingSalesRequested()];
    }
    if (viewMode === ClosingViewMode.COLLECTIONS) {
      return [findClosingCollectionsRequested()];
    }
    return [findClosingSalesRequested(), findClosingCollectionsRequested()];
  }, [viewMode]);

  const onedriveAction = useCallback(() => {
    if (viewMode === ClosingViewMode.SALES) {
      return [findClosingSalesRequested(true)];
    }
    if (viewMode === ClosingViewMode.COLLECTIONS) {
      return [findClosingCollectionsRequested(true)];
    }
    return [findClosingSalesRequested(true), findClosingCollectionsRequested(true)];
  }, [viewMode]);

  const loadingSelector = () => {
    if (viewMode === ClosingViewMode.SALES) {
      return reportClosingSalesLoadingSelector;
    }
    if (viewMode === ClosingViewMode.COLLECTIONS) {
      return reportClosingCollectionsLoadingSelector;
    }
    return reportClosingSalesLoadingSelector || reportClosingCollectionsLoadingSelector;
  };

  return (
    <ReportFilters
      loadingSelector={loadingSelector()}
      applyAction={applyAction}
      onedriveAction={onedriveAction}
      showOpStructureFilters
    >
      <PermissionsProvider resourcePermissions={[AppPermissions.MISSING_CLOSING_DETAIL]}>
        <FilterPanel
          subtitleTranslationKey="missing-closing-detail.filters.subtitle"
          actions={
            <StatusChip
              status={applyDisabled ? 'warning' : 'success'}
              label={
                applyDisabled ? t('filters.status.missingRequired') : t('filters.status.ready')
              }
            />
          }
        >
          <Grid container spacing={2.5} alignItems="flex-start">
            <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
              <ViewModeFilter fullWidth />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
              <CountryFilter fullWidth />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3} xl={3}>
              <StoreFilter />
            </Grid>
            <>
              <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                <FederativeEntityFilter fullWidth />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                <FranchiseTypeFilterRedux fullWidth />
              </Grid>
            </>
            <Grid item xs={12} md={8} lg={5} xl={4}>
              <BusinessDateRangeFilter
                spacing={1}
                requiredIncomplete={businessDateRequiredIncomplete}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
              <CauseTypeFilter />
            </Grid>
            <ReportActionsGridItem>
              <ReportFilterActions
                applyDisabled={applyDisabled}
                onedriveDisabled={applyDisabled}
                hasCountry={country}
              />
            </ReportActionsGridItem>
          </Grid>
        </FilterPanel>
      </PermissionsProvider>
    </ReportFilters>
  );
};

export default ClosingSearch;
